import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, message, Tag, Spin, Button, Modal, Form, Input, Switch, Space, Popconfirm, Select, Tooltip } from 'antd';
import { KeyOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { tradeAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const AuthMethods = () => {
  const [authMethods, setAuthMethods] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAuthMethod, setEditingAuthMethod] = useState(null);
  const [deletingAuthMethod, setDeletingAuthMethod] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAuthMethods();
    fetchDataSources();
  }, []);

  useEffect(() => {
    fetchAuthMethods();
  }, [pagination.current, pagination.pageSize]);

  const fetchAuthMethods = async () => {
    try {
      const response = await tradeAPI.getAuthMethods();
      const allData = response.data || [];
      const total = allData.length;

      // 前端分页计算
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const currentPageData = allData.slice(startIndex, endIndex);

      setAuthMethods(currentPageData);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      message.error('获取认证方法失败');
      console.error('获取认证方法失败:', error);
      setAuthMethods([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const fetchDataSources = async () => {
    try {
      const response = await tradeAPI.getDataSources();
      setDataSources(response.data);
    } catch (error) {
      message.error('获取数据源失败');
      console.error('获取数据源失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAuthMethod(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingAuthMethod(record);
    form.setFieldsValue({
      source_id: record.source_id,
      auth_key: record.auth_key,
      auth_type: record.auth_type,
      priority: record.priority,
      enabled: record.enabled,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    setDeletingAuthMethod(record);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAuthMethod) return;

    try {
      await tradeAPI.deleteAuthMethod(deletingAuthMethod.id);
      message.success('认证方法删除成功');
      setIsDeleteModalVisible(false);
      setDeletingAuthMethod(null);
      fetchAuthMethods();
    } catch (error) {
      message.error('删除认证方法失败');
      console.error('删除认证方法失败:', error);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingAuthMethod(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingAuthMethod) {
        // 更新认证方法
        await tradeAPI.updateAuthMethod(editingAuthMethod.id, values);
        message.success('认证方法更新成功');
      } else {
        // 创建认证方法
        await tradeAPI.createAuthMethod(values);
        message.success('认证方法创建成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchAuthMethods();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
      console.error('操作失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const getDataSourceName = (sourceId) => {
    const dataSource = dataSources.find((ds) => ds.id === sourceId);
    return dataSource ? dataSource.name : '未知数据源';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '数据源',
      dataIndex: 'source_id',
      key: 'source_id',
      render: (sourceId) => getDataSourceName(sourceId),
      width: 160,
    },
    {
      title: '认证标识',
      dataIndex: 'auth_key',
      key: 'auth_key',
      width: 200,
    },
    {
      title: '认证类型',
      dataIndex: 'auth_type',
      key: 'auth_type',
      width: 200,
      render: (text) => {
        const truncatedText = text && text.length > 10 ? `${text.substring(0, 10)}...` : text;
        return (
          <Tooltip placement="topLeft" title={text}>
            {truncatedText}
          </Tooltip>
        );
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => <Tag color={enabled ? 'green' : 'red'}>{enabled ? '启用' : '禁用'}</Tag>,
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={3}>认证方法管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建认证方法
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={authMethods}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />

      <Modal title={editingAuthMethod ? '编辑认证方法' : '新建认证方法'} open={isModalVisible} onOk={handleModalOk} onCancel={handleModalCancel}>
        <Form form={form} layout="vertical">
          <Form.Item name="source_id" label="数据源" rules={[{ required: true, message: '请选择数据源' }]}>
            <Select placeholder="请选择数据源">
              {dataSources.map((dataSource) => (
                <Option key={dataSource.id} value={dataSource.id}>
                  {dataSource.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="auth_key" label="认证标识" rules={[{ required: true, message: '请输入认证标识' }]}>
            <Input placeholder="输入认证方法的唯一标识符，用于其他页面选择" />
          </Form.Item>

          <Form.Item name="auth_type" label="认证类型" rules={[{ required: true, message: '请输入认证类型' }]}>
            <Input placeholder="如：API_KEY, OAUTH, JWT等" />
          </Form.Item>

          <Form.Item name="priority" label="优先级" rules={[{ required: true, message: '请输入优先级' }]}>
            <Input type="number" placeholder="优先级数字，越小优先级越高" />
          </Form.Item>

          <Form.Item name="enabled" label="启用状态" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="确认删除" open={isDeleteModalVisible} onOk={handleConfirmDelete} onCancel={handleCancelDelete} okText="确认删除" cancelText="取消" okType="danger">
        <p>确定要删除认证方法 "{deletingAuthMethod?.auth_type}" 吗？</p>
        <p style={{ color: 'red', marginTop: '8px' }}>此操作无法撤销，删除后将永久失去该认证方法及其所有相关数据。</p>
      </Modal>
    </div>
  );
};

export default AuthMethods;
